import React, { FC, useState, MouseEvent, useCallback } from 'react';
import styled from 'styled-components';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY, IPriceTier } from '@kouto/types';
import { isImageRatioGreaterSixteenNine } from 'utils';
import { Flex } from 'components/common/styled/common-styled';
import { SecondaryButton } from 'components/theme/Button/Button';
import { useHistory } from 'react-router-dom';
import { ExclusiveTagOverlay } from 'components/common/ExclusiveTagOverlay';
import {
  analyticsManager,
  getProductFromResourceGroups,
} from 'features/analytics/services';
import { truncateString } from 'utils/string';
import { formatListingMedia } from 'utils/listings';
import { NormalizedPublicListing } from 'types/listingV3';
import { useIsMobile } from 'WindowDimensionProvider';
import { OldLandingPageImage } from 'components/molecules/OldLandingPageImage/OldLandingPageImage';
import {
  applyMobileFontSize,
  FontSizeProps,
} from '../theme/Typography/ResourceTypography';

interface Props {
  resourceGroupCollectionId: string;
  medias: NormalizedPublicListing['medias'];
  title: string;
  preselectGroupOnClick: boolean;
  collectionResourceGroups: Array<{
    id: string;
    title: string;
  }>;
  exclusiveTagName: string | null;
  categoryName?: string;
  priceTiers?: IPriceTier[];
  index?: number;
}

const CollectionItem: FC<Props> = ({
  resourceGroupCollectionId,
  medias,
  title,
  preselectGroupOnClick,
  categoryName,
  priceTiers,
  collectionResourceGroups,
  exclusiveTagName,
  index,
}) => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const [isSixteenNineRatio, setIsSixteenNineRatio] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const formattedMedias = formatListingMedia(medias);
  const coverMedia = formattedMedias.find((media) => media.kind === 'cover');

  const onImgLoadSixteenNineRatio = (
    e: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    if (e.target instanceof HTMLImageElement) {
      setIsSixteenNineRatio(
        isImageRatioGreaterSixteenNine(
          e.target.naturalWidth,
          e.target.naturalHeight,
        ),
      );
    }
  };

  const handleGroupClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>, groupId: string) => {
      e.stopPropagation();

      if (preselectGroupOnClick) {
        history.push(
          `/collection/${resourceGroupCollectionId}/group/${groupId}`,
        );
      } else {
        history.push(`/collection/${resourceGroupCollectionId}`);
      }
    },
    [resourceGroupCollectionId, preselectGroupOnClick, history],
  );

  const handleCollectionClick = () => {
    if (collectionResourceGroups.length === 1) {
      analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
        [ANALYTICS_PROPERTY.ExperienceOrder]: `${index}`,

        [ANALYTICS_PROPERTY.ExperienceId]: collectionResourceGroups[0]?.id,
        [ANALYTICS_PROPERTY.ExperienceTitle]:
          collectionResourceGroups[0]?.title,
        [ANALYTICS_PROPERTY.ExperienceCategory]: categoryName,
        [ANALYTICS_PROPERTY.Products]: JSON.stringify(
          getProductFromResourceGroups({
            groups: collectionResourceGroups,
            categoryName,
            collectionId: resourceGroupCollectionId,
            collectionName: title,
            priceTiers,
          }),
        ),
      });
    }
    history.push(`/collection/${resourceGroupCollectionId}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCollectionClick();
    }
  };

  return (
    <CollectionViewItem
      onClick={handleCollectionClick}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      onKeyDown={handleKeyDown}
    >
      <CardGroup className="collection-item__card-group">
        <CardImage className="collection-item__card-image">
          <CardImageRatio className="collection-item__card-image-ratio">
            {exclusiveTagName && isHovered ? (
              <ExclusiveTagOverlay>{exclusiveTagName}</ExclusiveTagOverlay>
            ) : null}
            <OldLandingPageImage
              src={isMobile ? coverMedia?.urlMobile : coverMedia?.urlDesktop}
              loading="lazy"
              alt={title}
              onLoad={onImgLoadSixteenNineRatio}
              isSixteenNineRatio={isSixteenNineRatio}
            />
          </CardImageRatio>
        </CardImage>
        <Flex
          direction="column"
          alignItem="flex-start"
          justifyContent="flex-start"
          gap={12}
        >
          <Title mobileFontSize="16px" className="collection-item__title">
            {title}
          </Title>
          <ResourceGroupButtonWrapper
            direction="row"
            alignItem="flex-start"
            justifyContent="flex-start"
            gap={10}
          >
            {collectionResourceGroups.map((resourceGroup) => {
              return (
                <StyledSecondaryButton
                  key={resourceGroup.id}
                  className="collection-item__resource-group"
                  onClick={(e) => handleGroupClick(e, resourceGroup.id)}
                  title={resourceGroup.title}
                >
                  {truncateString(resourceGroup.title, 30)}
                </StyledSecondaryButton>
              );
            })}
          </ResourceGroupButtonWrapper>
        </Flex>
      </CardGroup>
    </CollectionViewItem>
  );
};

const CollectionViewItem = styled.div`
  cursor: pointer;
  max-width: calc(100vw - 24px);
`;

const CardGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardImage = styled.div`
  margin-bottom: 20px;
  border-radius: var(--way-design-borderRadiusDefault);
  background: inherit;
  position: relative;
`;

const CardImageRatio = styled.div`
  padding-bottom: 56.25%;
  overflow: hidden;
  position: relative;
`;

const Title = styled.h3<FontSizeProps>`
  font-size: 20px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
  margin: 0;
  color: var(--way-colors-primaryTextColor);

  ${(props) => applyMobileFontSize(props)};
`;

const ResourceGroupButtonWrapper = styled(Flex)`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;

  scrollbar-width: thin;
`;

const StyledSecondaryButton = styled(SecondaryButton)<FontSizeProps>`
  padding: 8px 10px;
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  line-height: normal;
  border: solid 0.5px;
  white-space: nowrap;
  flex-shrink: 0;

  ${(props) => applyMobileFontSize(props)};
`;

export default CollectionItem;
